import { debounce } from 'lodash';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, HideText, Icon } from '@fashionnovateam/nova-ui-toolkit';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import VendorTeamMembersDataGrid from './VendorTeamMembersDataGrid.tsx';
import Roles, { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';
import { NewMemberDrawer } from './NewMemberDrawer.tsx';
import { RolesAndPermissionDrawer } from './RolesAndPermissionDrawer.tsx';
import { RemoveContactDialog } from './RemoveContactDialog.tsx';
import UserAPI from '../../Lib/Api/user';
import toast from '../Toast/Toast';
import formatErrorMessage from '../../Lib/Utils/Helpers/format-error-message';

/**
 * Creates the table headers from objects imported here.
 *
 * @param {arrayOf} headerArr array of header value objects
 *  @property {object} header individual objects with header values
 *    @property {string} label the label value
 *    @property {number} key unique key for react components
 *
 * @returns {object} header cell components to be used by the table.
 */

const filterItems = [
  {
    icon: 'file/file-text',
    value: 'active',
    label: 'Active',
  },
  {
    icon: 'three-dots-menu/three-dots-menu-horizontal-circle',
    value: 'deactivated',
    label: 'Deactivated',
  },
];

export default function VendorTeamMembers(props) {
  const {
    currentUser,
    user,
    vendor,
    match: {
      params: { id: idOriginallyFromState },
    },
    setVendor,
  } = props;
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    search: '',
    page: 1,
    filters: 'active',
  });
  const [isMemberCreationDrawerOpen, setIsMemberCreationDrawerOpen] =
    useState(false);
  const [isRoleDrawerOpen, setIsRoleDrawerOpen] = useState(false);
  const [isRemoveContactDialogOpen, setIsRemoveContactDialogOpen] =
    useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const perms = useMemo(() => {
    const canCreate = hasPermissionTo(
      'users/:id/team-members',
      currentUser.role,
      Actions.CREATE,
      'MEMBER',
    );
    return {
      canCreate,
    };
  }, [currentUser]);

  const filteredContacts = useMemo(
    () =>
      vendor.contacts.filter((contact) => {
        const searchTerm = params.search.toLowerCase();
        const isDeactivated = !!contact.deleted_at;
        const matchesSearch =
          `${contact.first_name} ${contact.last_name}`
            .toLowerCase()
            .includes(searchTerm) ||
          contact.email.toLowerCase().includes(searchTerm) ||
          contact.role.toLowerCase().includes(searchTerm) ||
          (contact.title && contact.title.toLowerCase().includes(searchTerm)) ||
          (contact.phone && contact.phone.toLowerCase().includes(searchTerm));

        return (
          matchesSearch &&
          (params.filters === 'deactivated' ? isDeactivated : !isDeactivated)
        );
      }),
    [vendor.contacts, params.search, params.filters],
  );

  const openDrawerForNewMember = () => {
    setContactToEdit(null);
    setIsMemberCreationDrawerOpen(true);
  };

  const openDrawerForEdit = (contact) => {
    setContactToEdit(contact);
    setIsMemberCreationDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsMemberCreationDrawerOpen(false);
    setContactToEdit(null);
  };

  const saveContact = async (contact) => {
    const userId = idOriginallyFromState;
    setIsLoading(true);
    if (contactToEdit) {
      try {
        const response = await UserAPI.updateVendorContact(
          userId,
          contactToEdit.id,
          {
            email: contact.email,
            first_name: contact.first_name,
            last_name: contact.last_name,
            phone: contact.phone,
            phone_country: contact.phone_country,
            role: contact.role,
            title: contact.title,
            feature_permissions: {
              allow_printing: contact.feature_permissions?.allow_printing,
            },
          },
        );
        setVendor(response.vendor);
        toast('Profile successfully updated.', {
          type: 'success',
        });
      } catch (e) {
        toast(
          {
            title: 'Profile update failed.',
            text: formatErrorMessage(e),
          },
          {
            type: 'error',
          },
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const response = await UserAPI.createVendorContact(
          userId,
          contact,
          true,
        );
        setVendor(response.vendor);
        toast(
          {
            title: `We’ve sent an invite to ${contact.first_name} ${contact.last_name}.`,
            text: 'An email has been sent with an invitation to set up their account.',
          },
          {
            type: 'success',
          },
        );
      } catch (e) {
        toast(
          {
            title: 'Creating a new team member failed.',
            text: formatErrorMessage(e),
          },
          {
            type: 'error',
          },
        );
      } finally {
        setIsLoading(false);
      }
    }
    closeDrawer();
  };

  const openRemoveDialog = (contact) => {
    setContactToEdit(contact);
    setIsRemoveContactDialogOpen(true);
  };
  const removeContact = async (contact) => {
    try {
      setIsLoading(true);
      const response = await UserAPI.removeVendorContact(
        idOriginallyFromState,
        contact.id,
      );
      setVendor(response.vendor);
      toast(
        {
          title: `${contact.first_name} ${contact.last_name} has been deactivated.`,
          text: 'They no longer have access to their account.',
        },
        {
          type: 'success',
        },
      );
    } catch (e) {
      toast(
        {
          title: 'Deactivating member failed.',
          text: formatErrorMessage(e),
        },
        {
          type: 'error',
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canCreateAdmin = useMemo(
    () =>
      vendor.contacts.filter(
        (contact) => !contact.deleted_at && contact.role === Roles.VENDOR,
      ).length < 2,
    [vendor.contacts],
  );

  return (
    <Page.Container>
      <Header.Major icon="building/building-apartment-one" title="Team Members">
        {perms.canCreate && (
          <>
            <Button
              color="secondary"
              onClick={() => setIsRoleDrawerOpen(true)}
              size="sm"
            >
              <Icon name="bulb/bulb-on" variant="solid" />
              <HideText notAbove="lg">
                Learn{'\u00A0'}about{'\u00A0'}roles{'\u00A0'}
              </HideText>
            </Button>
            <Button onClick={openDrawerForNewMember} size="sm">
              <Icon name="plus/plus-square" variant="solid" />
              <HideText notAbove="lg">
                Add{'\u00A0'}a{'\u00A0'}member{'\u00A0'}
              </HideText>
            </Button>
          </>
        )}
      </Header.Major>
      <Header.Minor
        filter={{
          value: params.filters,
          onChange: (value) =>
            setParams((p) => ({
              ...p,
              filters: value,
            })),
          items: filterItems,
        }}
        search={{
          value: params.search,
          onChange: debounce(
            (value) => setParams((p) => ({ ...p, search: value })),
            500,
          ),
        }}
      />
      <Page.Content>
        <RemoveContactDialog
          isOpen={isRemoveContactDialogOpen}
          onClose={() => setIsRemoveContactDialogOpen(false)}
          contactToEdit={contactToEdit}
          removeContact={removeContact}
        />
        <VendorTeamMembersDataGrid
          loading={isLoading}
          rows={filteredContacts}
          params={params}
          currentUser={currentUser}
          onEditContact={openDrawerForEdit}
          remove={openRemoveDialog}
          count={filteredContacts.length}
          user={user}
        />
      </Page.Content>
      <RolesAndPermissionDrawer
        isOpen={isRoleDrawerOpen}
        onClose={() => setIsRoleDrawerOpen(false)}
      />
      <NewMemberDrawer
        isOpen={isMemberCreationDrawerOpen}
        onSave={saveContact}
        onClose={closeDrawer}
        existingContacts={vendor.contacts.filter(
          (contact) => !contact.deleted_at,
        )}
        canCreateAdmin={canCreateAdmin}
        contactToEdit={contactToEdit}
        loading={isLoading}
        user={user}
      />
    </Page.Container>
  );
}

VendorTeamMembers.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  currentUser: PropTypes.shape({}).isRequired,
};
